// Unified Task Types
// Used for both project-based and personnel-based (teamwork) task views

export type TaskStatus = 'plan' | 'doing' | 'review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  plan: 'Plan',
  doing: 'Doing',
  review: 'Review',
  done: 'Done',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  plan: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  doing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  done: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300',
};

// Unified Task interface - same as backend internal/task/types.go
export interface ITask {
  id: string;
  task_code: string;
  task_name: string;
  task_owner: string;

  // Project context (optional)
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;

  // Status and priority
  status: TaskStatus;
  priority: TaskPriority;

  // Time tracking
  duration: number; // hours
  planned_start: string; // YYYY-MM-DD
  planned_end: string; // YYYY-MM-DD
  actual_start?: string;
  actual_end?: string;

  // Dependencies
  prior_deps: string[]; // Task IDs that must complete before this
  post_deps: string[]; // Task IDs that depend on this

  // Organization
  sort_order: number;
  tags?: string[];

  // Attachments
  review_folder_id?: string;

  // Audit fields
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Request types
export interface ITaskCreateRequest {
  task_name: string;
  task_owner: string;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  duration?: number;
  planned_start?: string;
  planned_end?: string;
  prior_deps?: string[];
  post_deps?: string[];
  tags?: string[];
}

export interface ITaskUpdateRequest {
  task_name?: string;
  task_owner?: string;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  duration?: number;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  prior_deps?: string[];
  post_deps?: string[];
  tags?: string[];
  sort_order?: number;
}

export interface ITaskMoveRequest {
  status: TaskStatus;
  sort_order: number;
  phase_id?: string;
}

export interface ITaskOrder {
  id: string;
  sort_order: number;
}

export interface ITaskReorderRequest {
  orders: ITaskOrder[];
}

// Response types
export interface ITaskListResponse {
  items: ITask[];
  total: number;
  limit: number;
  offset: number;
}

export interface ITaskGroupedResponse {
  grouped: ITasksByStatus;
  total: number;
}

export interface ITasksByStatus {
  plan: ITask[];
  doing: ITask[];
  review: ITask[];
  done: ITask[];
}

export interface ITaskByOwnerResponse {
  owner: string;
  tasks: ITask[];
  grouped: ITasksByStatus;
  total: number;
}

export interface ITaskByProjectResponse {
  project_id: string;
  tasks: ITask[];
  grouped: ITasksByStatus;
  total: number;
}

export interface ITaskByPhaseResponse {
  project_id: string;
  phase_id: string;
  tasks: ITask[];
  grouped: ITasksByStatus;
  total: number;
}

export interface ITaskStats {
  total: number;
  by_status: Record<TaskStatus, number>;
  by_priority: Record<TaskPriority, number>;
  by_owner: Record<string, number>;
  overdue: number;
}

export interface IDependencyCheck {
  all_complete: boolean;
  incomplete_names?: string[];
  incomplete_ids?: string[];
  blocking_message?: string;
}

// Filter type for listing tasks
export interface ITaskFilter {
  project_id?: string;
  phase_id?: string;
  task_owner?: string;
  status?: TaskStatus;
  statuses?: TaskStatus[];
  priority?: TaskPriority;
  tags?: string[];
  include_completed?: boolean;
  grouped?: boolean;
  limit?: number;
  offset?: number;
}
